import { useEffect, useState } from 'react';
import EChart from '../../components/Charts/EChart';
import { analyticsApi } from '../../services/api';
import toast from 'react-hot-toast';

export default function RetentionTab() {
  const [loading, setLoading] = useState(true);
  const [retentionData, setRetentionData] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const data = await analyticsApi.retention();
      setRetentionData(data);
    } catch (error) {
      console.error('Failed to load retention analytics:', error);
      toast.error('Failed to load retention analytics');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!retentionData?.cohorts || retentionData.cohorts.length === 0) {
    return (
      <div className="rounded-lg bg-white p-12 shadow text-center">
        <p className="text-gray-500">No retention data available yet.</p>
        <p className="text-sm text-gray-400 mt-2">Data will appear as users create accounts over time.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <MetricCard
          title="Total Cohorts"
          value={retentionData.cohorts.length}
          subtitle="Signup month cohorts tracked"
          icon="📅"
        />
        <MetricCard
          title="Avg Month-1 Retention"
          value={`${calculateAvgRetention(retentionData.cohorts, 'month_1_retention')}%`}
          subtitle="Average 1-month retention"
          icon="📊"
        />
        <MetricCard
          title="Avg Month-3 Retention"
          value={`${calculateAvgRetention(retentionData.cohorts, 'month_3_retention')}%`}
          subtitle="Average 3-month retention"
          icon="🎯"
        />
      </div>

      {/* Retention Heatmap Visualization */}
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900">Cohort Retention Heatmap</h2>
          <p className="text-sm text-gray-600 mt-1">
            Visual representation of retention rates across cohorts and time periods
          </p>
        </div>
        <EChart option={transformRetentionHeatmap(retentionData)} height="400px" />
      </div>

      {/* Cohort Retention Table */}
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900">Cohort Retention Details</h2>
          <p className="text-sm text-gray-600 mt-1">
            Detailed percentage of users from each cohort that remain active in subsequent months
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cohort (Signup Month)
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Month 0
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Month 1
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Month 2
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Month 3
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {retentionData.cohorts.map((cohort, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCohortMonth(cohort.cohort_month)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                    {cohort.cohort_size ? cohort.cohort_size.toLocaleString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <RetentionCell value={100} /> {/* Month 0 is always 100% */}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <RetentionCell value={cohort.month_1_retention} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <RetentionCell value={cohort.month_2_retention} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <RetentionCell value={cohort.month_3_retention} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Retention Insights */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Best Performing Cohort</h3>
          {(() => {
            const best = findBestCohort(retentionData.cohorts);
            if (!best) return <p className="text-gray-500">Not enough data</p>;
            return (
              <div>
                <p className="text-2xl font-bold text-green-600 mb-2">
                  {formatCohortMonth(best.cohort_month)}
                </p>
                <dl className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <dt className="text-gray-600">Cohort Size</dt>
                    <dd className="font-semibold">{best.cohort_size ? best.cohort_size.toLocaleString() : 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-gray-600">Month 3 Retention</dt>
                    <dd className="font-semibold text-green-600">
                      {best.month_3_retention?.toFixed(1) || 'N/A'}%
                    </dd>
                  </div>
                </dl>
              </div>
            );
          })()}
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Retention Trend</h3>
          {(() => {
            const trend = calculateRetentionTrend(retentionData.cohorts);
            return (
              <div>
                <p className={`text-2xl font-bold mb-2 ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {trend >= 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}%
                </p>
                <p className="text-sm text-gray-600">
                  {trend >= 0 ? 'Improving' : 'Declining'} compared to previous cohorts
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Based on month-1 retention comparison
                </p>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Legend */}
      <div className="rounded-lg bg-gray-50 p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Color Legend</h4>
        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: getRetentionColor(80) }}></div>
            <span>80-100%: Excellent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: getRetentionColor(60) }}></div>
            <span>60-79%: Good</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: getRetentionColor(40) }}></div>
            <span>40-59%: Fair</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: getRetentionColor(20) }}></div>
            <span>20-39%: Poor</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: getRetentionColor(10) }}></div>
            <span>0-19%: Critical</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RetentionCell({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 text-sm">N/A</span>;
  }

  const color = getRetentionColor(value);
  const textColor = value >= 60 ? 'text-white' : 'text-gray-900';

  return (
    <span
      className={`inline-block px-3 py-1 rounded font-semibold text-sm ${textColor}`}
      style={{ backgroundColor: color }}
    >
      {value.toFixed(1)}%
    </span>
  );
}

function getRetentionColor(value) {
  if (value >= 80) return '#10b981'; // green-500
  if (value >= 60) return '#84cc16'; // lime-500
  if (value >= 40) return '#f59e0b'; // amber-500
  if (value >= 20) return '#f97316'; // orange-500
  return '#ef4444'; // red-500
}

function MetricCard({ title, value, subtitle, icon }) {
  return (
    <div className="rounded-lg bg-white p-6 shadow card-hover">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="text-3xl font-bold text-gray-900">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      <p className="text-sm text-gray-500 mt-2">{subtitle}</p>
    </div>
  );
}

function formatCohortMonth(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
}

function calculateAvgRetention(cohorts, field) {
  const validValues = cohorts
    .map(c => c[field])
    .filter(v => v !== null && v !== undefined);

  if (validValues.length === 0) return 'N/A';

  const avg = validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
  return avg.toFixed(1);
}

function findBestCohort(cohorts) {
  return cohorts.reduce((best, cohort) => {
    if (!cohort.month_3_retention) return best;
    if (!best || (cohort.month_3_retention > (best.month_3_retention || 0))) {
      return cohort;
    }
    return best;
  }, null);
}

function calculateRetentionTrend(cohorts) {
  if (cohorts.length < 2) return 0;

  // Compare the last 2 cohorts' month-1 retention
  const recent = cohorts[cohorts.length - 1]?.month_1_retention;
  const previous = cohorts[cohorts.length - 2]?.month_1_retention;

  if (!recent || !previous) return 0;

  return recent - previous;
}

function transformRetentionHeatmap(data) {
  if (!data?.cohorts || data.cohorts.length === 0) {
    return {
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'category', data: [] },
      series: []
    };
  }

  // Prepare data for heatmap
  const cohorts = data.cohorts;
  const yAxisData = cohorts.map(cohort => formatCohortMonth(cohort.cohort_month));
  const xAxisData = ['Month 0', 'Month 1', 'Month 2', 'Month 3'];

  // Transform data into [x, y, value] format for heatmap
  const heatmapData = [];
  cohorts.forEach((cohort, yIndex) => {
    // Month 0 is always 100%
    heatmapData.push([0, yIndex, 100]);

    // Month 1
    if (cohort.month_1_retention !== null && cohort.month_1_retention !== undefined) {
      heatmapData.push([1, yIndex, Math.round(cohort.month_1_retention * 10) / 10]);
    }

    // Month 2
    if (cohort.month_2_retention !== null && cohort.month_2_retention !== undefined) {
      heatmapData.push([2, yIndex, Math.round(cohort.month_2_retention * 10) / 10]);
    }

    // Month 3
    if (cohort.month_3_retention !== null && cohort.month_3_retention !== undefined) {
      heatmapData.push([3, yIndex, Math.round(cohort.month_3_retention * 10) / 10]);
    }
  });

  // Find min and max values for color scale (excluding 100% to focus on actual retention range)
  const retentionValues = heatmapData
    .filter(item => item[2] < 100)
    .map(item => item[2]);
  const minValue = retentionValues.length > 0 ? Math.min(...retentionValues) : 0;
  const maxValue = retentionValues.length > 0 ? Math.max(...retentionValues) : 100;

  return {
    tooltip: {
      position: 'top',
      formatter: (params) => {
        const cohortName = yAxisData[params.data[1]];
        const month = xAxisData[params.data[0]];
        const retention = params.data[2];
        return `${cohortName}<br/>${month}: ${retention.toFixed(1)}%`;
      }
    },
    grid: {
      left: '15%',
      right: '10%',
      bottom: '15%',
      top: '5%'
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
      splitArea: {
        show: true
      },
      axisLabel: {
        fontSize: 12,
        fontWeight: 'bold'
      }
    },
    yAxis: {
      type: 'category',
      data: yAxisData,
      splitArea: {
        show: true
      },
      axisLabel: {
        fontSize: 11
      }
    },
    visualMap: {
      min: minValue,
      max: 100,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: '0%',
      inRange: {
        color: ['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981']
      },
      text: ['High', 'Low'],
      textStyle: {
        fontSize: 11
      }
    },
    series: [
      {
        name: 'Retention',
        type: 'heatmap',
        data: heatmapData,
        label: {
          show: true,
          formatter: (params) => {
            return params.data[2] ? `${params.data[2].toFixed(1)}%` : 'N/A';
          },
          fontSize: 11,
          fontWeight: 'bold'
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  };
}
